import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import apiService from '../../services/api';

interface Device {
  id: string;
  model: string;
  serialNumber: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  warrantyExpires?: string;
  customerName?: string;
}

interface DeviceSelectorProps {
  companyId: string | null;
  selectedDeviceId: string | null;
  onDeviceSelect: (device: Device | null) => void;
  loading?: boolean;
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({ 
  companyId, 
  selectedDeviceId, 
  onDeviceSelect, 
  loading = false 
}) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (companyId) {
      loadCompanyDevices();
    } else {
      setDevices([]);
      onDeviceSelect(null);
    }
  }, [companyId]);

  const loadCompanyDevices = async () => {
    if (!companyId) return;
    
    setLoadingDevices(true);
    setError('');
    
    try {
      console.log(`🔍 Loading devices for company ID: ${companyId}`);
      const response = await apiService.getDevices({ 
        companyId, 
        limit: 100,
        page: 1 
      });
      
      console.log(`✅ Loaded ${response.devices.length} devices for company ${companyId}`);
      console.log('🔧 Device models from API:', response.devices.map(d => d.model));
      setDevices(response.devices || []);
    } catch (err: any) {
      console.error('Error loading devices:', err);
      setError('Failed to load devices for this company');
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleDeviceSelect = (device: Device) => {
    // Pass device model as-is without mapping to preserve configurable device models
    console.log(`🔧 DeviceSelector: Selected device with model "${device.model}"`);
    onDeviceSelect(device);
  };

  const handleClearSelection = () => {
    onDeviceSelect(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const isWarrantyExpired = (warrantyDate?: string) => {
    if (!warrantyDate) return false;
    return new Date(warrantyDate) < new Date();
  };

  const isWarrantyExpiringSoon = (warrantyDate?: string) => {
    if (!warrantyDate) return false;
    const warrantDate = new Date(warrantyDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return warrantDate > now && warrantDate <= thirtyDaysFromNow;
  };

  if (!companyId) {
    return (
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select a company to view available devices
        </p>
      </div>
    );
  }

  if (loadingDevices) {
    return (
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Loading devices...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        <button 
          onClick={loadCompanyDevices}
          className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No devices found for this company
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Company Devices ({devices.length})
        </label>
        {selectedDeviceId && (
          <button
            onClick={handleClearSelection}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Clear selection
          </button>
        )}
      </div>
      
      <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        {devices.map((device) => {
          const isSelected = selectedDeviceId === device.id;
          const warrantyExpired = isWarrantyExpired(device.warrantyExpires);
          const warrantyExpiringSoon = isWarrantyExpiringSoon(device.warrantyExpires);
          
          return (
            <div
              key={device.id}
              onClick={() => handleDeviceSelect(device)}
              className={`p-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0 cursor-pointer transition-colors ${
                isSelected 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' 
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                      {device.model}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      #{device.serialNumber}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">Invoice:</span>{' '}
                      {device.invoiceNumber || 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Date:</span>{' '}
                      {formatDate(device.invoiceDate)}
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Warranty:</span>
                      <span className={`${
                        warrantyExpired 
                          ? 'text-red-600 dark:text-red-400' 
                          : warrantyExpiringSoon 
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {formatDate(device.warrantyExpires)}
                      </span>
                      {warrantyExpired && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          Expired
                        </span>
                      )}
                      {warrantyExpiringSoon && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Expiring Soon
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {isSelected && (
                  <div className="ml-2 flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {selectedDeviceId && (
        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            ✓ Device selected and will be linked to this ticket
          </p>
        </div>
      )}
    </div>
  );
};

export default DeviceSelector;
